import { IconButton } from '@monorepo/shared/componentsV2/Button/IconButton';
import { ErrorIcon } from '@monorepo/shared/componentsV2/errorDisplays/ErrorIcon';
import { FilePreview } from '@monorepo/shared/componentsV2/FilePreview';
import { ModalOverlayLoading } from '@monorepo/shared/componentsV2/Loading';
import { useSkipWidgetLink } from '@monorepo/shared/componentsV2/SkipWidgetLink';
import { VisuallyHidden } from '@monorepo/shared/componentsV2/VisuallyHidden';
import { EditEntryForm } from '@monorepo/shared/componentsV2/logs/EditEntryModal/EditEntryForm';
import { EditLogEntryBuiltInFields } from '@monorepo/shared/componentsV2/logs/EditLogEntryModal/EditLogEntryBuiltInFields';
import { LogEntry } from '@monorepo/shared/componentsV2/logs/types';
import { useLogEntryFields } from '@monorepo/shared/hooks/logs/useLogFields';
import { useCurrentUser } from '@monorepo/shared/hooks/useCurrentUser';
import CompletedIcon from '@svg/completed.svg';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';
import { useFormState } from 'react-final-form';
import styled from 'styled-components';
import { LogEntriesLoadingOverlay } from '../LogEntriesLoadingOverlay';
import { LogEntriesFormValues } from '../conversionHelpers';
import { usePersistClearedFormFieldValues } from '../hooks/usePersistClearedFormFieldValues';
import { useSyncEditedLogEntryIds } from '../hooks/useSyncEditedLogEntryIds';
import { type SelectedFile } from '../hooks/useUploadedFiles';
import { EntryStatus, getLogEntryStatus } from '../utils/logEntryStatus';
import {
  CurrentEntrySubtitle,
  CurrentEntrySubtitleFileName,
  CurrentEntrySubtitleText,
  EntryNavigationButton,
  LogEntriesDocumentContainer,
  LogEntriesEditFormContainer,
  LogEntriesNavigationSidebar,
  MainLayoutContainer,
  SingleEditLogEntryForm,
  StyledTextIndentIcon,
  StyledTextOutdentIcon,
} from '../styled';

type LogEntriesEditManyModalPageProps = {
  selectedFile?: SelectedFile;
  logEntryIndex: number;
  setLogEntryIndex: (index: number) => void;
  logId: string;
  organizationId: string;
  projectId: string;
  isReadOnly: boolean;
  forceShowErrors?: boolean;
  isSubmitting?: boolean;
  handleMapLogEntryFormValuesIntoLogEntries?: (
    formValues: Partial<LogEntriesFormValues>,
  ) => LogEntry[];
  setLogEntries?: (logEntries: LogEntry[]) => void;
  areAllFilesCompletedOrHaveErrors?: boolean;
  hasAnyErrorFiles?: boolean;
  navigateToFileUploadErrorPage?: () => void;
  setEditedLogEntryIds?: Dispatch<SetStateAction<Set<string>>>;
};

const StatusIconContainer = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  width: 0.75rem;
  height: 0.75rem;
  margin-right: 0.625rem;

  & > * {
    width: 100%;
    height: 100%;
    margin: auto;
  }
`;

const StatusDot = styled.span`
  width: 0.65rem;
  height: 0.65rem;
  background-color: ${({ theme }) => theme.colors.green};
  border-radius: 50%;
`;

const StyledCheckIcon = styled(CompletedIcon)`
  fill: ${({ theme }) => theme.colors.green};
`;

// Status is otherwise conveyed by icon color and shape alone, which screen
// reader users can't perceive.
const ENTRY_STATUS_LABELS: Record<EntryStatus, string> = {
  [EntryStatus.INCOMPLETE]: 'incomplete',
  [EntryStatus.COMPLETE_REVIEWED]: 'complete, reviewed',
  [EntryStatus.COMPLETE_NOT_REVIEWED]: 'complete, not reviewed',
};

function getEntryStatusIcon(status: EntryStatus) {
  switch (status) {
    case EntryStatus.INCOMPLETE:
      return (
        <StatusIconContainer>
          <ErrorIcon aria-hidden />
        </StatusIconContainer>
      );
    case EntryStatus.COMPLETE_NOT_REVIEWED:
      return (
        <StatusIconContainer>
          <StatusDot aria-hidden />
        </StatusIconContainer>
      );
    case EntryStatus.COMPLETE_REVIEWED:
      return (
        <StatusIconContainer>
          <StyledCheckIcon aria-hidden />
        </StatusIconContainer>
      );
    default:
      return null;
  }
}

export function LogEntriesEditManyModalPage({
  selectedFile,
  logEntryIndex,
  setLogEntryIndex,
  logId,
  organizationId,
  projectId,
  isReadOnly,
  forceShowErrors,
  isSubmitting,
  handleMapLogEntryFormValuesIntoLogEntries,
  setLogEntries,
  areAllFilesCompletedOrHaveErrors,
  hasAnyErrorFiles,
  navigateToFileUploadErrorPage,
  setEditedLogEntryIds,
}: LogEntriesEditManyModalPageProps) {
  const { logFields } = useLogEntryFields({
    organizationId,
    logId,
  });
  const { currentUser } = useCurrentUser();
  const [isNavigationSidebarVisible, setIsNavigationSidebarVisible] =
    useState(true);
  const toggleNavigationSidebar = useCallback(
    () => setIsNavigationSidebarVisible(!isNavigationSidebarVisible),
    [isNavigationSidebarVisible],
  );
  const { SkipLink, SkipLinkTarget } = useSkipWidgetLink();
  const currentLogEntries = selectedFile?.logEntries;
  const currentLogEntry = currentLogEntries?.[logEntryIndex];
  const allLogEntryIds = useMemo(
    () => currentLogEntries?.map((entry) => entry.id) ?? [],
    [currentLogEntries],
  );

  const { errors, values } = useFormState({
    subscription: { errors: true, values: true },
  });

  useSyncEditedLogEntryIds(setEditedLogEntryIds, allLogEntryIds);
  usePersistClearedFormFieldValues();

  useEffect(() => {
    if (
      !setLogEntries ||
      !currentUser?.id ||
      !currentLogEntries ||
      !currentLogEntry
    ) {
      return;
    }
    if (isReadOnly) return;
    if (currentLogEntry.reviewedBy) return;

    setLogEntries(
      currentLogEntries.map((entry) =>
        entry.id === currentLogEntry.id
          ? { ...entry, reviewedBy: currentUser.id }
          : entry,
      ),
    );
  }, [
    currentLogEntries,
    currentLogEntry,
    currentUser?.id,
    isReadOnly,
    setLogEntries,
  ]);

  // Sync form values to logEntries when switching between entries so that
  // cleared fields (empty string) are preserved when returning to an entry.
  const syncFormValuesToLogEntries = useCallback(
    (formValues: Partial<LogEntriesFormValues>) => {
      if (handleMapLogEntryFormValuesIntoLogEntries && setLogEntries) {
        setLogEntries(handleMapLogEntryFormValuesIntoLogEntries(formValues));
      }
    },
    [handleMapLogEntryFormValuesIntoLogEntries, setLogEntries],
  );

  const handleEntryChange = useCallback(
    (newIndex: number) => {
      if (values) {
        syncFormValuesToLogEntries(values);
      }
      setLogEntryIndex(newIndex);
    },
    [setLogEntryIndex, values, syncFormValuesToLogEntries],
  );

  useEffect(() => {
    if (
      areAllFilesCompletedOrHaveErrors &&
      hasAnyErrorFiles &&
      navigateToFileUploadErrorPage
    ) {
      navigateToFileUploadErrorPage();
    }
  }, [
    areAllFilesCompletedOrHaveErrors,
    hasAnyErrorFiles,
    navigateToFileUploadErrorPage,
  ]);

  if (!currentLogEntries) {
    return <LogEntriesLoadingOverlay isReadOnly={isReadOnly} />;
  }

  return (
    <>
      {isSubmitting && <ModalOverlayLoading />}
      <SkipLink linkText="Skip to the modal footer" />
      <CurrentEntrySubtitle>
        <IconButton onClick={toggleNavigationSidebar}>
          {isNavigationSidebarVisible ? (
            <>
              <StyledTextOutdentIcon />
              <VisuallyHidden>Hide entry list</VisuallyHidden>
            </>
          ) : (
            <>
              <StyledTextIndentIcon />
              <VisuallyHidden>Show entry list</VisuallyHidden>
            </>
          )}
        </IconButton>
        <CurrentEntrySubtitleText>
          Entry {logEntryIndex + 1} of {currentLogEntries.length}
        </CurrentEntrySubtitleText>
        <CurrentEntrySubtitleFileName>
          {/* For TS: name does not exist on ExistingFile */}
          {selectedFile?.type === 'UploadedFile' && selectedFile.name}
        </CurrentEntrySubtitleFileName>
      </CurrentEntrySubtitle>
      <MainLayoutContainer>
        <LogEntriesEditFormContainer>
          {isNavigationSidebarVisible && (
            <LogEntriesNavigationSidebar>
              {currentLogEntries.map((logEntry, index) => {
                const status = getLogEntryStatus({
                  logEntry,
                  errors,
                  formValues: values,
                  logFields,
                });
                return (
                  <EntryNavigationButton
                    key={logEntry.id}
                    onClick={() => handleEntryChange(index)}
                    $isActive={index === logEntryIndex}
                  >
                    {getEntryStatusIcon(status)}
                    Entry {index + 1}
                    <VisuallyHidden>
                      , {ENTRY_STATUS_LABELS[status]}
                    </VisuallyHidden>
                  </EntryNavigationButton>
                );
              })}
            </LogEntriesNavigationSidebar>
          )}
          {logFields && currentLogEntry && (
            <SingleEditLogEntryForm>
              <EditEntryForm
                entryTypeDisplayName="Log entry"
                fieldValues={currentLogEntry.logFieldValues}
                fields={logFields}
                organizationId={organizationId}
                projectId={projectId}
                fieldNamePrefix={currentLogEntry.id}
                shouldHideInstructions
                isReadOnly={isReadOnly}
                isSubtextHidden
                forceShowErrors={forceShowErrors}
              >
                <EditLogEntryBuiltInFields
                  existingLogEntry={currentLogEntry}
                  fieldNamePrefix={currentLogEntry.id}
                  disabled={isReadOnly}
                  isErrorTextHidden={false}
                  forceShowErrors={forceShowErrors}
                />
              </EditEntryForm>
            </SingleEditLogEntryForm>
          )}
        </LogEntriesEditFormContainer>

        {selectedFile?.fileBlobUrl && (
          <LogEntriesDocumentContainer>
            <FilePreview
              src={selectedFile.fileBlobUrl}
              title="Log Entries Document"
              mimeType={selectedFile.mimeType}
              fileName={
                selectedFile.type === 'UploadedFile'
                  ? selectedFile.name
                  : undefined
              }
            />
          </LogEntriesDocumentContainer>
        )}
        <SkipLinkTarget />
      </MainLayoutContainer>
    </>
  );
}
